// EditableShape adapter for ContextPin objects from ZoneState
// Supports move only — no resize or rotate (pins are simple point markers)

import type { EditableShape } from '@/types/editor'
import type { ContextPin } from '@/types/zone'
import { zoneState } from '@/zones/ZoneState'
import { getPinTypeDef } from '@/constants/zoneTypes'
import { undoStack } from '../UndoStack'
import { debug } from '@/utils/debug'

export class PinAdapter implements EditableShape {
  readonly sourceType = 'pin' as const
  private pinId: string

  private snapshot: { x: number; y: number } | null = null

  constructor(pin: ContextPin) {
    this.pinId = pin.id
  }

  get id(): string {
    return this.pinId
  }

  private getPin(): ContextPin {
    const pin = zoneState.getPinById(this.pinId)
    if (!pin) throw new Error(`PinAdapter: pin ${this.pinId} not found`)
    return pin
  }

  // --- Read geometry ---

  getCenter(): { x: number; y: number } {
    const p = this.getPin()
    return { x: p.x, y: p.y }
  }

  getSize(): { width: number; height: number } {
    return { width: 0, height: 0 }
  }

  getRotation(): number {
    return 0
  }

  getShapeKind(): 'rectangle' | 'ellipse' | 'polygon' {
    return 'rectangle'
  }

  getPoints(): Array<{ x: number; y: number }> | null {
    return null
  }

  // --- Write geometry ---

  moveTo(x: number, y: number): void {
    zoneState.updatePin(this.pinId, { x, y })
  }

  resize(_width: number, _height: number): void {
    // No-op: pins have no size
  }

  rotate(_degrees: number): void {
    // No-op: pins don't rotate
  }

  setPoints(_points: Array<{ x: number; y: number }>): void {
    // No-op: pins don't have polygon points
  }

  // --- Capabilities ---

  isResizable(): boolean {
    return false
  }

  // --- Visual ---

  getAccentColor(): number {
    const pin = this.getPin()
    const def = getPinTypeDef(pin.pinType)
    return def.color
  }

  // --- Undo integration ---

  beginEdit(): void {
    const p = this.getPin()
    this.snapshot = { x: p.x, y: p.y }
  }

  commit(): void {
    if (!this.snapshot) return

    const before = this.snapshot
    const p = this.getPin()
    const after = { x: p.x, y: p.y }

    if (before.x === after.x && before.y === after.y) {
      this.snapshot = null
      return
    }

    const id = this.pinId
    undoStack.push({
      description: `Move pin ${id}`,
      undo: () => zoneState.updatePin(id, before),
      redo: () => zoneState.updatePin(id, after),
    })

    debug.log('PinAdapter', `Committed edit for ${id}`)
    this.snapshot = null
  }

  cancel(): void {
    if (!this.snapshot) return
    zoneState.updatePin(this.pinId, this.snapshot)
    debug.log('PinAdapter', `Cancelled edit for ${this.pinId}`)
    this.snapshot = null
  }
}
